import React from "react";
import {
  Button,
  Dropdown,
  Modal,
  Typography,
  MenuProps,
  Card,
  Tag,
} from "antd";
import ApplicationStageSteps from "./ApplicationStageSteps";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import {
  JobApplication,
  getRejectionReasonLabel,
} from "../../../../Entities/JobApplication";
import { MatchResult } from "../../../../Entities/MatchResult";
import { Session } from "../../../../Entities/Session";
import { DownOutlined } from "@ant-design/icons";
import { InterviewRound } from "../../../../Dto/GenerateJobDescription.dto";
import { openNotification } from "../../../../Components/Notification";
import useOrganization from "../../../../Hooks/useOrganization";
import { getCurrentApplicationStatusMessage } from "../../utils/job.application.helper.functions";

interface JobApplicationSummaryActionsProps {
  jobPost: JobDescriptionEntity;
  jobApplication: JobApplication;
  interviewSessions: Session[];
  interviewRounds: InterviewRound[];
  handleMoveToAIAssessmentRound: () => Promise<void>;
  handleRestartAIAssessmentRound: () => Promise<void>;
  handleMarkAsShortlisted: () => Promise<void>;
  handleMarkAsRejected: () => Promise<void>;
  handleMarkAsHired: () => Promise<void>;
  handleAllowInterviewSchedule: () => Promise<void>;
  setActiveTabKey: React.Dispatch<React.SetStateAction<string>>;
  profileMatchResult: MatchResult | null;
}

const JobApplicationSummaryActions: React.FC<
  JobApplicationSummaryActionsProps
> = ({
  jobPost,
  jobApplication,
  interviewSessions,
  interviewRounds,
  handleMoveToAIAssessmentRound,
  handleRestartAIAssessmentRound,
  handleMarkAsShortlisted,
  handleMarkAsRejected,
  handleMarkAsHired,
  handleAllowInterviewSchedule,
  setActiveTabKey,
  profileMatchResult,
}) => {
  const { org } = useOrganization();

  const isProfileMatchPending =
    !jobApplication.profileId ||
    !jobApplication.profileMatchStatus ||
    jobApplication.profileMatchStatus.status === "pending";

  const validateJobApplicationStatus = (
    status: "applied" | "shortlisted" | "interview" | "rejected" | "hired",
  ) => {
    return jobApplication?.status === status;
  };

  const handleStageSelectionConfirmation = async (
    action:
      | "move.to.ai.assessment.round"
      | "restart.ai.assessment.round"
      | "allow.interview.schedule"
      | "mark.as.shortlisted"
      | "mark.as.rejected"
      | "mark.as.hired",
  ) => {
    switch (action) {
      case "move.to.ai.assessment.round":
        return await handleMoveToAIAssessmentRound();
      case "restart.ai.assessment.round":
        return await handleRestartAIAssessmentRound();
      case "allow.interview.schedule":
        return await handleAllowInterviewSchedule();
      case "mark.as.shortlisted":
        return await handleMarkAsShortlisted();
      case "mark.as.rejected":
        return await handleMarkAsRejected();
      case "mark.as.hired":
        return await handleMarkAsHired();
      default:
        return;
    }
  };

  const handleStageSelection = (
    action:
      | "move.to.ai.assessment.round"
      | "restart.ai.assessment.round"
      | "allow.interview.schedule"
      | "mark.as.shortlisted"
      | "mark.as.rejected"
      | "mark.as.hired",
  ) => {
    let confirmationContent: React.ReactNode = null;
    let okButtonText = "Confirm";

    switch (action) {
      case "move.to.ai.assessment.round":
        okButtonText = "Move to AI Assessment";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Move the application to the AI assessment round</li>
              <li>Generate interview sessions for the candidate to complete</li>
              <li>Permanently change the application status</li>
              <li>
                Send an email notification to the candidate about the assessment
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "restart.ai.assessment.round":
        okButtonText = "Restart AI Assessment";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Restart the AI assessment round for this candidate</li>
              <li>Reset all previous assessment data for this round</li>
              <li>Require the candidate to complete the assessment again</li>
              <li>
                Send an email notification to the candidate about the restart
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "allow.interview.schedule":
        okButtonText = "Allow Interview Schedule";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Allow the candidate to schedule their interview</li>
              <li>
                Enable the candidate to select an available interview time slot
              </li>
              <li>Change the application status to "Interview"</li>
              <li>
                Send an email notification to the candidate with scheduling
                instructions
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "mark.as.shortlisted":
        okButtonText = "Mark as Shortlisted";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark the application as shortlisted</li>
              <li>Move the candidate to the shortlisted stage</li>
              <li>Consider the candidate for further interview rounds</li>
              <li>Permanently change the application status</li>
              <li>
                Send an email notification to the candidate about being
                shortlisted
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "mark.as.rejected":
        okButtonText = "Mark as Rejected";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark the application as rejected</li>
              <li>Move the candidate to the rejected stage</li>
              <li>Remove the candidate from consideration for this position</li>
              <li>Permanently change the application status</li>
              <li>
                Send an email notification to the candidate about the rejection
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      case "mark.as.hired":
        okButtonText = "Mark as Hired";
        confirmationContent = (
          <div>
            <p style={{ marginBottom: "12px", fontWeight: 500 }}>
              This action will:
            </p>
            <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
              <li>Mark the application as hired</li>
              <li>Move the candidate to the hired stage</li>
              <li>
                Indicate the candidate has been selected for this position
              </li>
              <li>Permanently change the application status</li>
              <li>
                Send an email notification to the candidate about being hired
              </li>
            </ul>
            <p style={{ marginBottom: 0, color: "var(--ant-color-error)" }}>
              <strong>Note:</strong> This action cannot be undone.
            </p>
          </div>
        );
        break;
      default:
        break;
    }

    if (confirmationContent) {
      Modal.confirm({
        title: "Confirm Action",
        content: confirmationContent,
        okText: okButtonText,
        cancelText: "Cancel",
        icon: null,
        centered: true,
        okButtonProps: {
          style: {
            backgroundColor:
              org?.theme?.primaryColor || "var(--ant-color-primary)",
            borderColor: org?.theme?.primaryColor || "var(--ant-color-primary)",
          },
        },
        onOk: () => {
          handleStageSelectionConfirmation(action);
        },
        onCancel: () => {
          return;
        },
      });
    } else {
      openNotification("error", "Error", "Failed to change application stage");
      return;
    }
  };

  const items: MenuProps["items"] = [];

  if (jobPost?.onlineInterview) {
    const isProfileMatchFailed =
      jobApplication?.profileMatchStatus?.status === "unqualified";
    const isCustomRoundsEnabled = interviewRounds.length > 0;

    const isEnableInterviewSceduleForNoCustomRounds =
      !isProfileMatchFailed && !isCustomRoundsEnabled;

    items.push({
      key: "allow.interview.schedule",
      label: "Allow Interview Schedule",
      disabled: isEnableInterviewSceduleForNoCustomRounds,
      onClick: () => handleStageSelection("allow.interview.schedule"),
    });
  } else {
    items.push({
      key: "mark.as.shortlisted",
      label: "Mark as Shortlisted",
      disabled:
        validateJobApplicationStatus("shortlisted") ||
        validateJobApplicationStatus("hired") ||
        isProfileMatchPending,
      onClick: () => handleStageSelection("mark.as.shortlisted"),
    });
  }

  items.push(
    {
      key: "mark.as.rejected",
      label: "Mark as Rejected",
      disabled:
        validateJobApplicationStatus("rejected") ||
        validateJobApplicationStatus("hired"),
      onClick: () => handleStageSelection("mark.as.rejected"),
    },
    {
      key: "mark.as.hired",
      label: "Mark as Hired",
      disabled: validateJobApplicationStatus("hired") || isProfileMatchPending,
      onClick: () => handleStageSelection("mark.as.hired"),
    },
  );

  const isPartiallyQualified =
    jobApplication?.questionnaireStatus === "partially_qualified";

  return (
    <Card className="job-application-summary-actions-container-progress">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography.Title level={5} style={{ margin: 0 }}>
          Application Overview
        </Typography.Title>

        {!isPartiallyQualified && (
          <Dropdown
            menu={{
              items,
              selectable: false,
            }}
          >
            <Button type="primary">
              Update Status &nbsp;
              <DownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>

      {jobApplication &&
        jobApplication.rejectionReason &&
        jobApplication.status === "rejected" && (
          <div className="rejection-reason-container">
            <Typography.Text type="danger">
              {getRejectionReasonLabel(jobApplication?.rejectionReason)}
            </Typography.Text>

            {/* <Tag color="red">{jobApplication.rejectionReason}</Tag> */}
          </div>
        )}
      <div key="application-stages" style={{ width: "100%" }}>
        <ApplicationStageSteps
          jobPost={jobPost}
          jobApplication={jobApplication}
          interviewSessions={interviewSessions}
          currentStatusMessage={getCurrentApplicationStatusMessage(
            jobApplication,
          )}
          setActiveTabKey={setActiveTabKey}
          profileMatchResult={profileMatchResult}
        />
      </div>
    </Card>
  );
};

export default JobApplicationSummaryActions;
